import { z } from 'zod';
import { ProjectStatus } from '../enums.js';
import { fieldSourceKind, fieldStatus } from './prefill.js';
import { projectSummary } from './project.js';
/**
 * Dashboard-Contracts (Paket M Phase 4 ff.).
 *
 * - stats:        KPI-Karten + Fristen-Liste (inkl. ueberfaellige Fristen).
 * - status-summary: Aggregation ueber ALLE sichtbaren Projekte (Donut +
 *   Ø-Vollstaendigkeit) — nicht nur ueber die zuletzt geladenen 50.
 * - activity:    7-Tage-Aktivitaetsreihe fuer das Dashboard-Bar-Chart.
 * - ai-proposals: paginierte, filterbare Liste offener KI-Vorschlaege ueber
 *   alle sichtbaren Projekte — Datengrundlage der /ai-proposals-Inbox.
 */
// ---- Stats (KPI-Karten + Fristen-Liste) -----------------------------------
/**
 * Eintrag der Fristen-Liste. `daysUntil` ist die Tagesdifferenz auf
 * Tagesgrenzen-Basis (UTC): negativ = ueberfaellig, 0 = heute faellig.
 */
export const dashboardDeadlineItem = projectSummary.extend({
    daysUntil: z.number().int().nullable(),
    /** true wenn die Frist vor dem heutigen Tagesbeginn liegt (daysUntil < 0). */
    overdue: z.boolean(),
});
/**
 * Dokument-Kennzahlen der Dashboard-Kachel.
 *
 * WARUM drei Zahlen statt einer: die frühere Einzelzahl zählte nur Dokumente
 * MIT Projektzuordnung und wurde als „Dokumente" beschriftet — im Eingang
 * lagen dann sichtbar mehr Dateien, als die Kachel behauptete. Wer eine
 * Kennzahl nicht mit der Liste abgleichen kann, glaubt keiner von beiden.
 *
 * Sichtbarkeit identisch zu `GET /documents`: sichtbare Projekte ODER eigene
 * Uploads. Deshalb kann `unassigned` > 0 sein, obwohl der Nutzer kein einziges
 * fremdes Inbox-Dokument sieht.
 */
export const dashboardDocumentCounts = z.object({
    /** Alle für den Nutzer sichtbaren Dokumente. */
    total: z.number().int().min(0),
    /** Davon einem Projekt zugeordnet. */
    assigned: z.number().int().min(0),
    /** Davon noch ohne Projekt (Eingang). `total - assigned`. */
    unassigned: z.number().int().min(0),
});
export const dashboardStatsResponse = z.object({
    activeProjects: z.number().int().min(0),
    /** Fristen im Fenster [heute 00:00, heute + 30 Tage] (Tagesgrenzen, UTC). */
    upcomingDeadlines: z.number().int().min(0),
    /** Ueberfaellige Fristen (vor heutigem Tagesbeginn, Projekt nicht abgeschlossen). */
    overdueDeadlines: z.number().int().min(0),
    aiSuggestions: z.number().int().min(0),
    documents: dashboardDocumentCounts,
    recentProjects: z.array(projectSummary),
    /** Ueberfaellige zuerst (aelteste Frist oben), danach kommende aufsteigend. */
    upcomingDeadlinesList: z.array(dashboardDeadlineItem),
});
// ---- Status-Summary (Donut + Ø-Vollstaendigkeit) --------------------------
export const dashboardStatusCount = z.object({
    status: ProjectStatus,
    count: z.number().int().min(0),
});
export const dashboardStatusSummaryResponse = z.object({
    /** Gesamtzahl sichtbarer Projekte (alle Status). */
    total: z.number().int().min(0),
    /** Ein Eintrag je vorkommendem Status; Status ohne Projekte fehlen. */
    byStatus: z.array(dashboardStatusCount),
    /** Anzahl aktiver (nicht abgeschlossener) Projekte — Basis des Durchschnitts. */
    activeTotal: z.number().int().min(0),
    /**
     * Ø-Vollstaendigkeit ueber aktive Projekte, gerundet auf ganze Prozent.
     * null wenn keine aktiven Projekte existieren.
     */
    avgActiveCompleteness: z.number().int().min(0).max(100).nullable(),
});
// ---- Activity (7-Tage-Reihe) --------------------------------------------
export const dashboardActivityPoint = z.object({
    /** ISO-Timestamp (UTC-Tagesbeginn). */
    date: z.string().datetime(),
    count: z.number().int().min(0),
});
export const dashboardActivityResponse = z.object({
    points: z.array(dashboardActivityPoint),
});
// ---- AI-Proposals-Inbox -------------------------------------------------
/** Nur die offenen (= vorgeschlagenen) Status sind fuer die Inbox relevant. */
export const proposedStatus = z.enum([
    'proposed_ai',
    'proposed_clone',
    'proposed_input',
    'proposed_legal',
]);
export const aiProposalsQuery = z.object({
    /** Filter auf eine Vorschlags-Quelle (Provenienz). */
    source: fieldSourceKind.optional(),
    /** Nur Vorschlaege mit confidence >= diesem Wert (0..100, Prozent). */
    confidenceMin: z.coerce.number().int().min(0).max(100).optional(),
    limit: z.coerce.number().int().positive().max(200).default(100),
    offset: z.coerce.number().int().min(0).default(0),
});
export const aiProposal = z.object({
    projectId: z.number().int().positive(),
    projectTitle: z.string(),
    fieldKey: z.string(),
    status: fieldStatus,
    source: fieldSourceKind,
    proposedValue: z.unknown(),
    confidence: z.number().nullable(),
    reason: z.string().nullable(),
    createdAt: z.string().datetime(),
});
export const aiProposalsResponse = z.object({
    items: z.array(aiProposal),
    total: z.number().int().min(0),
});
